import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Calendar, Clock, Monitor, Sprout, Smartphone, Heart, 
  Compass, Mic, Gift, Star, Check, ChevronRight, ArrowRight
} from "lucide-react";
import vinodProfile from "@/assets/vinod-profile.jpg";
import harmonyAppPreview from "@/assets/harmony-app-preview.png";

// ============================================
// WEBINAR SCHEDULE CONFIGURATION
// Update these values for future webinars
// ============================================
const WEBINAR_CONFIG = {
  title: "Introducing Siwon Harmony",
  day: "Every Saturday",
  startTime: "7:30 PM",
  endTime: "9:00 PM",
  duration: "90 minutes",
  timezone: "IST",
  platform: "Online · Free"
};

// Webinar series starts on this date — earlier Saturdays are not shown
const WEBINAR_SERIES_START = new Date(Date.UTC(2026, 4, 30, 14, 0)); // 30 May 2026, 7:30 PM IST = 14:00 UTC

// Returns "Sat, May 30" — the upcoming Saturday in IST.
// If today is Saturday and it's already past 7:30 PM IST, return the FOLLOWING Saturday.
// If computed date falls before WEBINAR_SERIES_START, returns the series start date instead.
const getNextWebinarLabel = () => {
  const now = new Date();
  // Convert to IST (UTC+5:30) regardless of viewer's locale
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
  const ist = new Date(utcMs + 5.5 * 60 * 60 * 1000);

  const dow = ist.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = ist.getHours();
  const minute = ist.getMinutes();
  const pastCutoff = hour > 19 || (hour === 19 && minute >= 30);

  let daysUntilSaturday;
  if (dow === 6) {
    daysUntilSaturday = pastCutoff ? 7 : 0;
  } else {
    daysUntilSaturday = (6 - dow + 7) % 7;
    if (daysUntilSaturday === 0) daysUntilSaturday = 7;
  }

  const next = new Date(ist);
  next.setDate(ist.getDate() + daysUntilSaturday);

  // Respect series start date
  if (next.getTime() < WEBINAR_SERIES_START.getTime()) {
    return WEBINAR_SERIES_START.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", timeZone: "UTC"
    });
  }
  return next.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

// ============================================
// CTA LINKS
// ============================================
const LINKS = {
  webinarRegistration: "https://learn.siwongalaxy.com/web/checkout/696794e19fa315cfdf89597b",
  silverMembership: "https://learn.siwongalaxy.com/web/checkout/68f3350b67ef17436efc34a9",
  harmonyApp: "https://app.siwongalaxy.com"
};

// What awaits you - features
const webinarFeatures = [
  {
    icon: Sprout,
    title: "The Story Behind Siwon Harmony",
    description: "Discover the vision, mission, and heartfelt journey behind creating a coaching app that truly understands people."
  },
  {
    icon: Smartphone,
    title: "Live App Demo",
    description: "See Siwon Harmony in action — explore the features, tools, and coaching pathways designed to transform your daily life."
  },
  {
    icon: Heart,
    title: "Relationship Breakthroughs",
    description: "Learn practical frameworks for building healthier, more conscious relationships — with partners, family, and yourself."
  },
  {
    icon: Compass,
    title: "Life Coaching Tools",
    description: "Walk away with real tools you can start using immediately to gain clarity and create a life aligned with your values."
  },
  {
    icon: Mic,
    title: "Live Q&A Session",
    description: "Bring your real questions. This is an open, intimate conversation — not a rehearsed pitch."
  },
  {
    icon: Gift,
    title: "Exclusive Early Access",
    description: "Attendees get a special early-access offer to Siwon Harmony — available only to those who show up live."
  }
];

// Silver membership benefits
const silverBenefits = [
  "Access to basic resources & tools",
  "Community forum access",
  "Monthly group coaching sessions",
  "Begin your transformation journey",
  "Exclusive webinar attendee pricing"
];

const FreeWebinar = () => {
  return (
    <div className="min-h-screen" data-testid="free-webinar-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-b from-primary via-teal-dark to-teal-dark mandala-bg relative overflow-hidden">
        <div className="absolute inset-0 mandala-bg opacity-30" />
        
        {/* Top Right CTA Button - Fixed position within hero */}
        <div className="absolute top-28 right-8 z-20 hidden lg:block">
          <a
            href={LINKS.webinarRegistration}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-lime inline-flex items-center justify-center gap-2 shadow-lg animate-pulse hover:animate-none"
            data-testid="top-right-register-btn"
          >
            Join Free Webinar
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
        
        <div className="container relative z-10 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-medium mb-2">
              <Calendar className="w-4 h-4" />
              Free Live Webinar · {WEBINAR_CONFIG.day}
            </div>
            <p className="text-primary-foreground/80 text-sm mb-6" data-testid="next-webinar-line">
              Next: <span className="font-semibold text-accent">{getNextWebinarLabel()}</span> · {WEBINAR_CONFIG.startTime} {WEBINAR_CONFIG.timezone}
            </p>
            
            <p className="text-accent text-lg font-medium mb-4">{WEBINAR_CONFIG.title}</p>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
              Reconnect with Yourself<br />
              <span className="text-accent italic">& Those You Love</span>
            </h1>
            
            <p className="text-lg md:text-xl text-primary-foreground/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              A free live webinar introducing <strong>Siwon Harmony</strong> — the relationship & life coaching app built to guide you toward clarity, deeper connection, and a life of intention.
            </p>
            
            {/* Schedule Cards */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <Calendar className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-primary-foreground font-semibold text-sm">{WEBINAR_CONFIG.day}</p>
                <p className="text-primary-foreground/60 text-xs">Mark your calendar</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <Clock className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-primary-foreground font-semibold text-sm">{WEBINAR_CONFIG.startTime} – {WEBINAR_CONFIG.endTime}</p>
                <p className="text-primary-foreground/60 text-xs">{WEBINAR_CONFIG.duration} · {WEBINAR_CONFIG.timezone}</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <Monitor className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-primary-foreground font-semibold text-sm">{WEBINAR_CONFIG.platform}</p>
                <p className="text-primary-foreground/60 text-xs">From anywhere</p>
              </div>
            </div>
            
            {/* CTA Button */}
            <a
              href={LINKS.webinarRegistration}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lime inline-flex items-center justify-center gap-2 text-lg px-10 py-5"
              data-testid="hero-register-btn"
            >
              Secure Your Free Spot
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* What Awaits You Section */}
      <section className="py-12 bg-background">
        <div className="container">
          <div className="text-center mb-10">
            <p className="text-accent font-medium mb-2">What Awaits You</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              This is not just a webinar.<br />
              <span className="text-accent italic">It's a turning point.</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {webinarFeatures.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <Card key={idx} className="border border-border hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Siwon Harmony App Preview */}
      <section className="py-12 bg-sage-light">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
            {/* App Preview */}
            <div className="flex justify-center">
              <div className="relative shadow-2xl rounded-2xl" style={{ width: '280px' }}>
                <img 
                  src={harmonyAppPreview} 
                  alt="Siwon Harmony App Preview" 
                  className="w-full h-auto block rounded-2xl"
                />
                <div className="absolute -inset-4 bg-purple-500/20 blur-2xl rounded-full -z-10"></div>
              </div>
            </div>
            
            {/* App Info */}
            <div>
              <p className="text-accent font-medium mb-2">Introducing</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
                Siwon Harmony App
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Your daily companion for relationship harmony and life transformation. Built on Vedic wisdom and modern psychology, the Siwon Harmony App guides you through personalized assessments, coaching pathways, and practical tools.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Five Koshas Assessment — understand your whole self</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Relationship & Life coaching modules</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Monthly progress tracking & insights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">Vedic wisdom meets modern tools</span>
                </li>
              </ul>
              <a
                href={LINKS.harmonyApp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-colors"
              >
                Explore the App
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Silver Membership Offer */}
      <section className="py-12 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-accent font-medium mb-2">Exclusive Webinar Offer</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
                Start Your Journey with<br />
                <span className="text-accent">Silver Membership</span>
              </h2>
              <p className="text-muted-foreground">
                Join our community and begin your transformation with exclusive webinar attendee benefits.
              </p>
            </div>
            
            <Card className="border-2 border-primary/30 shadow-lg">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  {/* Icon & Title */}
                  <div className="text-center md:text-left">
                    <div className="w-20 h-20 mx-auto md:mx-0 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <Star className="w-10 h-10 text-gray-500" />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-primary mb-2">Silver Membership</h3>
                    <p className="text-muted-foreground text-sm">Perfect for starting your journey</p>
                  </div>
                  
                  {/* Benefits */}
                  <div className="flex-grow">
                    <ul className="space-y-2">
                      {silverBenefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-foreground">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* CTA */}
                  <div className="text-center">
                    <a
                      href={LINKS.silverMembership}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-lime inline-flex items-center justify-center gap-2 px-8 py-4"
                      data-testid="silver-membership-btn"
                    >
                      Join Silver
                      <ChevronRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Your Host Section */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Star className="w-8 h-8 text-accent mx-auto mb-4" />
              <p className="text-accent font-medium mb-2">Your Host & Coach</p>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-8">
              <Avatar className="h-40 w-40 border-4 border-accent shadow-lg flex-shrink-0">
                <AvatarImage src={vinodProfile} alt="Vinod Kumar - Founder of Siwon Galaxy" />
                <AvatarFallback className="text-3xl font-serif text-primary bg-white">VK</AvatarFallback>
              </Avatar>
              
              <div className="text-center md:text-left">
                <h3 className="font-serif text-2xl font-bold mb-2">Vinod Kumar</h3>
                <p className="text-accent text-sm mb-4">Founder · Siwon Harmony · siwongalaxy.com</p>
                <p className="text-primary-foreground/85 leading-relaxed">
                  A passionate Relationship and Life WISDOM Coach dedicated to helping people create lives and connections that truly nourish them. With 20+ years of professional expertise and deep study of Vedantic wisdom, Vinod guides seekers through the SIWON methodology — helping them transform discord into harmony, confusion into clarity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 bg-sage-light">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
              The best time to begin is <span className="text-accent">now.</span>
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Over {WEBINAR_CONFIG.duration}, you'll gain clarity, connection, and a new tool that can change your life.
              <br />
              <strong>Seats are limited. Don't miss this.</strong>
            </p>
            
            {/* Schedule Reminder */}
            <div className="inline-flex items-center gap-4 bg-white rounded-full px-6 py-3 shadow-md mb-8">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-foreground font-medium">
                {WEBINAR_CONFIG.day} · {WEBINAR_CONFIG.startTime} {WEBINAR_CONFIG.timezone}
              </span>
            </div>
            
            <div className="block">
              <a
                href={LINKS.webinarRegistration}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lime inline-flex items-center justify-center gap-2 text-lg px-10 py-5"
                data-testid="final-register-btn"
              >
                Register for Free
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FreeWebinar;
